import React, { useState, useMemo, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import upload from '../../../Assets/images/bootstrap-upload.png';
import { responsiveSizeTimesheet, validationMessages } from '../../../utils/constants';
import classes from './ImageViewer.module.scss';
import { useWindowSize } from '../../../utils/hooks';
import AddAttachmentIcon from '../Icons/AddAttachmentIcon';
import NotificationModal from '../ModalWindows/NotificationModal';

export default function ImageViewer({
  uploadHandler,
  acceptedFormats = ['.jpeg', '.jpg', '.png', '.docx', '.pdf', '.xls', '.csv'],
  maxFileSizeMB = 10,
  isMultiple = true,
  allowedQtyOfAttachments = 10,
  existingAttachmentsQty = 9
}) {
  const [error, setError] = useState('');
  const [fileSet, setFiles] = useState([]);
  const size = useWindowSize();
  const [t] = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const megabyte = 1024 * 1024;

  const validateFiles = (filesList) => {
    if (!isMultiple && filesList.length > 1) {
      setError(validationMessage.CV_ONLY_ONE_FILE);
      return false;
    }

    if (
      Object.values(filesList).some((file) => {
        return file.size > maxFileSizeMB * megabyte;
      })
    ) {
      setError(
        `${t('profile.validations.FILE_SIZE_LIMIT1')}${maxFileSizeMB}${t('profile.validations.FILE_SIZE_LIMIT2')}`
      );
      t();
      return false;
    }

    if (
      Object.values(filesList).some((file) => {
        const tempArray = file.name.split('.');
        return !acceptedFormats.includes(`.${tempArray[tempArray.length - 1]}`);
      })
    ) {
      setError(
        `${t('profile.validations.FILE_EXTENSION_LIMIT1')}${acceptedFormats.join(', ').toUpperCase()} ${t(
          'profile.validations.FILE_EXTENSION_LIMIT2'
        )}`
      );
      return false;
    }

    if (existingAttachmentsQty === allowedQtyOfAttachments) {
      setError(
        `${t('profile.validations.FILES_QTY_LIMIT1')}${allowedQtyOfAttachments} ${t(
          'profile.validations.FILES_QTY_LIMIT2'
        )}`
      );
      return false;
    }

    setError('');
    return true;
  };

  const onDropHandler = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();

    const isValid = validateFiles(ev.dataTransfer.files);

    if (isValid) {
      setFiles((prev) => {
        return [...prev, ...ev.dataTransfer.files];
      });
      uploadHandler(ev.dataTransfer.files);
    }
  };

  const onChangeHandler = (ev) => {
    const { files } = ev.target;
    uploadHandler([...files]);
    setFiles((prev) => {
      return [...prev, ...files];
    });
  };

  const onDragOver = (ev) => ev.preventDefault();

  return (
    <div className={`${!fileSet.length ? classes.container : `${classes.container} ${classes.attachmentExist}`}`}>
      <form>
        <button
          className={`${classes.dropZone} ${!fileSet.length && 'text-center d-grid p-5'}`}
          onDrop={onDropHandler}
          onDragOver={onDragOver}
          type="button"
        >
          <label htmlFor="fileUploader" className={classes.labelContainer}>
            {size.width > responsiveSizeTimesheet ? (
              <>
                <img src={upload} alt="file upload" className={classes.uploadImage} />
                <div>
                  <label htmlFor="fileUploader" className={classes.phrase1}>
                    {t('profile.builder.start.drop')}&nbsp;
                  </label>
                  {!fileSet.length && <br />}
                  <label htmlFor="fileUploader" className={classes.phrase2}>
                    <Trans i18nKey="profile.builder.start.browse">
                      or<span> browse</span>
                    </Trans>
                  </label>
                </div>
              </>
            ) : (
              <div className={classes.mobileLabel}>
                <div className={classes.composableSvgContainer}>
                  <AddAttachmentIcon />
                </div>
                <p>{t('profile.builder.start.attachment')}</p>
                <div />
              </div>
            )}

            <input
              id="fileUploader"
              multiple={isMultiple}
              type="file"
              accept={acceptedFormats.join(',')}
              onChange={onChangeHandler}
              style={{ display: 'none' }}
              onClick={(event) => {
                event.currentTarget.value = null;
              }}
            />
          </label>
        </button>
      </form>
      {error && (
        <NotificationModal
          open={error}
          onClose={() => setError('')}
          paddingX={size.width > responsiveSizeTimesheet ? '36px' : '49px'}
          height={size.width > responsiveSizeTimesheet ? '450px' : '600px'}
        >
          <p className={classes.errorMessage}>{error}</p>
        </NotificationModal>
      )}
    </div>
  );
}
